import { apiFetch } from '@/services/core/apiFetch'
import { getContext } from '@/services/core/context'
import { throwApiError } from '@/services/core/apiError'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

/**
 * The preview popup's window name. A page reads `window.name` to tell whether
 * it is the preview window itself or a tab something else opened — the popup
 * has an opener of its own, so an opener alone says nothing.
 */
export const PREVIEW_WINDOW_NAME = 'adn-creative-preview'

/**
 * Tells the ad server to send a click back to the URL this names rather than
 * on to the creative's destination, so the click proves it can be counted.
 *
 * The ad server reads it off the Referer of the click, not off the ad request,
 * so it has to sit on the URL of the page the ad is rendered in.
 */
export const CLICK_CHECK_PARAM = 'preview-redirect-to-test-click-handling'

/**
 * The ad server renders previews, and it lives on its own host. Derive it from
 * the configured API host so switching environments keeps both in step, and let
 * an explicit env var win when a deployment pairs them differently.
 */
export function adServerBase(): string {
  const configured = import.meta.env.VITE_ADNUNTIUS_ADSERVER_BASE
  if (configured) return String(configured).replace(/\/$/, '')

  const api = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'
  const { protocol, hostname, port } = new URL(api)
  if (hostname === 'localhost') return `${protocol}//${hostname}:8078`
  if (hostname === 'api.adnuntius.com') return 'https://delivery.adnuntius.com'
  // api.<env>.adnuntius.com -> adserver.<env>.adnuntius.com
  return `${protocol}//${hostname.replace(/^api\./, 'adserver.')}${port ? `:${port}` : ''}`
}

/** An asset resolved into the shape the preview renderer expects for a component. */
export interface PreviewAssetValue {
  cdnId?: string
  previewCdnId?: string
  width?: number
  height?: number
  duration?: number
  bps?: number
  content?: string
}

export type PreviewLayoutParameter = string | PreviewAssetValue | null

export interface PreviewRequest {
  layoutId?: string
  renderTemplate?: string
  lineItemId?: string
  lineItemCode?: string
  creativeWidth: number
  creativeHeight: number
  video?: boolean
  advertiserName?: string
  controllingEntityName?: string
  electionName?: string
  electionDate?: string
  hasContextualTargeting?: boolean
  hasLocationTargeting?: boolean
  hasUserTargeting?: boolean
  layoutParameters: Record<string, PreviewLayoutParameter>
}

export interface LivePreviewRequest {
  viewUrl: string
  teamId: string
  layoutId: string
  width?: number
  height?: number
  layoutParameters: Record<string, PreviewLayoutParameter>
}

export const PreviewApi = {
  /**
   * The rendered HTML of a saved creative, as the ad server would deliver it.
   * Rejects with a validation error when the creative cannot serve.
   */
  getCreativeHtml: async (creativeId: string): Promise<string> => {
    const res = await apiFetch(
      `${API_BASE}/creativepreview/${encodeURIComponent(creativeId)}?context=${getContext()}`,
      { headers: { Accept: 'application/json' } }
    )
    if (!res.ok) await throwApiError(res, 'Failed to render creative preview')
    const data = await res.json()
    return (data.html as string) ?? ''
  },

  /**
   * Renders a preview straight from layout parameters, so unsaved edits and
   * layout render templates can be previewed before anything is stored.
   */
  render: async (request: PreviewRequest): Promise<string> => {
    // The ad server rejects a preview with no network, so a visitor who has never
    // signed in (the public political preview) borrows a placeholder, as UII does
    const network = getContext() || 'placeholder-network'
    const res = await fetch(`${adServerBase()}/preview?context=${network}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...request, networkId: network }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to render preview')
    return res.text()
  },

  /**
   * The app's preview window for a saved creative, rendered through adn.js
   * so the political label shows. It renders what is stored and indexed, so
   * unsaved edits do not show there.
   */
  blankPageUrl: (
    creativeId: string,
    options: { lineItemId?: string; width?: number; height?: number; name?: string } = {}
  ): string => {
    const params = new URLSearchParams({ network: getContext() })
    if (options.name) params.set('name', options.name)
    if (options.lineItemId) params.set('lineItem', options.lineItemId)
    if (options.width) params.set('width', String(options.width))
    if (options.height) params.set('height', String(options.height))
    return `/preview-creative/${encodeURIComponent(creativeId)}?${params}`
  },

  /** Opens the preview window as a popup, without the browser's tabs and toolbars. */
  openWindow: (url: string) => {
    const width = Math.min(1440, window.screen.availWidth)
    const height = Math.min(960, window.screen.availHeight)
    window.open(url, PREVIEW_WINDOW_NAME, `popup,width=${width},height=${height}`)
  },

  /** A link that shows this creative on a real page's ad units. */
  createLiveLink: async (request: LivePreviewRequest): Promise<string> => {
    const res = await apiFetch(`${API_BASE}/livepreview/create?context=${getContext()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...request, networkId: getContext() }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create live preview link')
    const data = await res.json()
    return (data.livePreviewLink as string) ?? ''
  },
}
